/**
 * Which formats the setup screen offers, and what tapping one does.
 *
 * The templates themselves are data in the engine (`FORMAT_TEMPLATES`), because a format is
 * a `RuleSet` and there is exactly one spelling of cricket law (ADR-0001). What this module
 * adds is the join the *client* needs: a template is a `RuleSet` **without an id**, and a
 * match references a RuleSet by id so a later rule edit cannot rewrite a finished scorecard
 * (PRD §6.5). So a card is only startable when the server has a row for it.
 *
 * That join is deliberately explicit rather than falling back to whatever the server sorts
 * first. A card reading "Box Cricket — 6 overs, 6 a side" that created a 20-over T20 match
 * would be the worst kind of bug: the screen and the ledger describing different games, with
 * nothing on either surface admitting it. Until the P1 format seed lands (#40), a template
 * the server does not have is shown and *not* selectable, and it says why.
 *
 * A guest scores locally with no server in the loop, so there the template is the whole
 * truth — see `localRuleSet`.
 */
import { FORMAT_TEMPLATES, asId } from "@sportsos/engine";
import type { FormatTemplate, RuleSet, RuleSetId } from "@sportsos/engine";

/** What the ball is made of. Not cricket law, so not a `RuleSet` field — but it changes
 *  how a match is remembered, and a tape-ball game on a matting wicket is the common case
 *  this product exists for (PRD §1.2). Local to the draft; the API carries no field for it. */
export type BallType = "leather" | "tennis" | "tape";

export const BALL_TYPES: readonly BallType[] = ["leather", "tennis", "tape"];

/** One card in the format row. */
export interface FormatChoice {
  /** Stable across renders and unique within the row — the template name is both. */
  readonly key: string;
  readonly template: FormatTemplate;
  /**
   * The server's row for this format, or null when it has none.
   *
   * Null means "shown, not startable while signed in": there is no id to pin the match to.
   */
  readonly ruleSet: RuleSet | null;
}

/**
 * The format row: every template the engine ships, joined to the server's RuleSets by name.
 *
 * Highest version wins when the server carries several, because that is the current spelling
 * of that competition's rules — an older row is kept only so completed matches still replay
 * against what they were scored under.
 *
 * Server RuleSets with no matching template (a league's own format) are appended rather than
 * dropped. A format that exists is one somebody chose, and hiding it would make the screen
 * quietly less capable than the account behind it.
 */
export function formatChoices(serverRuleSets: readonly RuleSet[]): readonly FormatChoice[] {
  const newest = new Map<string, RuleSet>();
  for (const rules of serverRuleSets) {
    const held = newest.get(rules.name);
    if (held === undefined || rules.version > held.version) newest.set(rules.name, rules);
  }

  const fromTemplates = FORMAT_TEMPLATES.map((template) => ({
    key: template.name,
    template,
    ruleSet: newest.get(template.name) ?? null,
  }));

  const named = new Set(FORMAT_TEMPLATES.map((template) => template.name));
  const fromServer = [...newest.values()]
    .filter((rules) => !named.has(rules.name))
    .map((rules) => ({ key: rules.name, template: withoutId(rules), ruleSet: rules }));

  return [...fromTemplates, ...fromServer];
}

/** A `RuleSet` read back as a template — the id is the one thing a template does not carry. */
export function withoutId(rules: RuleSet): FormatTemplate {
  const { id: _id, ...rest } = rules;
  return rest;
}

/**
 * The one-line summary under a format's name: `20 overs · 11 a side · 4 per bowler`.
 *
 * Overs first because it is the number a scorer picks a format *by*, and `null` overs is a
 * timed format rather than a missing value.
 */
export function describeFormat(template: FormatTemplate): string {
  const parts = [
    template.overs === null ? "no over limit" : `${template.overs} overs`,
    `${template.playersPerSide} a side`,
  ];
  if (template.maxOversPerBowler !== null) parts.push(`${template.maxOversPerBowler} per bowler`);
  return parts.join(" · ");
}

/**
 * The ball a format is usually played with, as the default for the ball-type chips.
 *
 * Inferred from the rules rather than stored on the template: a format that allows a catch
 * off one bounce is being played with something that bounces off a wall, and one that does
 * not enforce lbw on a six-a-side pitch is a tennis-ball game. The scorer can always say
 * otherwise — this only decides which chip starts lit.
 */
export function defaultBallType(template: FormatTemplate): BallType {
  if (template.oneBounceCatchOut) return "tape";
  if (template.oneTipOneHandOut) return "tennis";
  return "leather";
}

/**
 * A template as a `RuleSet` for **local** scoring, with a placeholder id.
 *
 * Used on the guest path, where nothing is published and the id names nothing on any server.
 * Deliberately not a uuid, for the same reason `T20` in `setup.ts` is not: a value that
 * cannot be mistaken for a server id cannot be sent to one by accident.
 */
export function localRuleSet(template: FormatTemplate): RuleSet {
  return { ...template, id: asId<RuleSetId>(`local-${slug(template.name)}`) };
}

const slug = (name: string): string => name.toLowerCase().replace(/[^a-z0-9]+/g, "-");

/** The rule fields a scorer may edit under "Customise rules". Everything else is structural. */
export type EditableRules = Pick<
  RuleSet,
  | "overs"
  | "ballsPerOver"
  | "maxOversPerBowler"
  | "playersPerSide"
  | "lbwEnabled"
  | "noBallFreeHit"
  | "wideRule"
  | "lastManStands"
  | "oneTipOneHandOut"
  | "oneBounceCatchOut"
  | "superOverOnTie"
>;

const EDITABLE: readonly (keyof EditableRules)[] = [
  "overs",
  "ballsPerOver",
  "maxOversPerBowler",
  "playersPerSide",
  "lbwEnabled",
  "noBallFreeHit",
  "wideRule",
  "lastManStands",
  "oneTipOneHandOut",
  "oneBounceCatchOut",
  "superOverOnTie",
];

/**
 * Whether the scorer has edited a format away from the template it started as.
 *
 * The signed-in path needs this as a *warning*, not a flag: there is no route that creates a
 * RuleSet (the contract has none), so a customised format cannot be pinned server-side and
 * the match would be created under the unedited row. Saying so is the only honest option —
 * silently discarding the edits is how a scorer finds out at the eleventh over that the
 * match is being scored to six-ball overs he changed to eight.
 */
export function isCustomised(rules: EditableRules, template: EditableRules): boolean {
  return EDITABLE.some((field) => rules[field] !== template[field]);
}

/**
 * The per-bowler cap that matches a given number of overs.
 *
 * Standard cricket convention: each bowler may bowl at most one-fifth of the innings.
 * `ceil` so short formats (6 or 8 overs) get 2 rather than 1, matching the templates
 * the engine ships. Returns null for unlimited-overs formats.
 */
export function suggestedMaxPerBowler(overs: number | null): number | null {
  if (overs === null) return null;
  return Math.max(1, Math.ceil(overs / 5));
}

/** A per-over cap the format cannot honour is a rule the engine would have to break. */
export function rulesProblem(rules: EditableRules): string | null {
  if (rules.overs !== null && rules.overs < 1) return "A match needs at least one over";
  if (rules.ballsPerOver < 1) return "An over needs at least one ball";
  if (rules.playersPerSide < 2) return "A side needs at least two players";
  if (
    rules.maxOversPerBowler !== null &&
    rules.overs !== null &&
    rules.maxOversPerBowler > rules.overs
  ) {
    return "A bowler cannot be allowed more overs than the innings has";
  }
  return null;
}
